<i18n src="./resources/locales.json"></i18n>
<template>
  <div class="ma-3">
    <div class="title">{{ $t('title') }}</div>
    <div class="d-flex flex-row flex-wrap align-center">
      <EcsEmailSignatureSelection class="flex-grow-1 mx-2 signature-header-item" />
      <EcsBusinessMultipleProcessSelection
        class="flex-grow-1 mx-2 signature-header-item"
        item-value="id"
        item-text="name"
        filter-media-type="Email"
        :label="$t('assignedBusinessProcesses')"
        :items="emailBusinessProcesses"
        :on-value-change="onBusinessProcessChange"
        :value="selectedBusinessProcessesIds"
      />
    </div>
    <div class="text-container pb-3">
      <div>{{ $t('followingParameters') }}</div>
      <div>{{ $t('templateWildcards') }}</div>
    </div>
    <div class="d-flex flex-row flex-wrap align-center" :style="{ width: isSmtpEnabled ? '' : '50%' }">
      <v-text-field
        class="flex-grow-1 mx-2 signature-edit-item"
        :label="$t('signatureName')"
        :value="name"
        :error="error"
        :error-messages="emptyError"
        @input="updateTemplateName"
      ></v-text-field>
      <EcsAssignedEmails
        class="flex-grow-1 mx-2 signature-edit-item"
        :is-smtp-enabled="isSmtpEnabled"
        :items="availableSmtpEmails"
        :value="assignedEmails"
        :onValueChange="onAssignedEmailsChange"
      />
    </div>
    <EcsConfirmSignatureOverwrite
      :show="showOverwriteDialog"
      :business-processes-overwrite="businessProcessesToOverwrite"
      :is-smtp-enabled="isSmtpEnabled"
      :on-cancel="cancelBPOverwrite"
      :on-confirm="confirmBPOverwrite"
    />
  </div>
</template>

<script>
import EcsBusinessMultipleProcessSelection from '@/components/ecs-business-process-selection/EcsBusinessMultipleProcessSelection';
import EcsEmailSignatureSelection from '@/components/ecs-email-signatures/EcsEmailSignatureSelection';
import EcsAssignedEmails from '@/components/ecs-email-signatures/EcsAssignedEmails';
import EcsConfirmSignatureOverwrite from '@/components/ecs-email-signatures/EcsConfirmSignatureOverwrite';
import { mapGetters, mapActions } from 'vuex';
import debounce from 'lodash/debounce';
import uniq from 'lodash/uniq';
const DEFAULT = 'default';

export default {
  name: 'EcsEmailSignaturesHeader',
  components: {
    EcsBusinessMultipleProcessSelection,
    EcsEmailSignatureSelection,
    EcsConfirmSignatureOverwrite,
    EcsAssignedEmails
  },
  data() {
    return {
      error: false,
      showOverwriteDialog: false,
      businessProcessesToOverwrite: [],
      selectedBusinessProcessesIds: [],
      assignedEmailsToSave: []
    };
  },
  watch: {
    businessProcessesIds(newValue) {
      this.selectedBusinessProcessesIds = newValue;
    }
  },
  created() {
    this.selectedBusinessProcessesIds = this.businessProcessesIds;
  },
  computed: {
    ...mapGetters([
      'businessProcesses',
      'selectedEmailSignature',
      'customizedSignatureBusinessProcesses',
      'smtpDetails'
    ]),
    businessProcessesIds() {
      if (!this.selectedEmailSignature || !this.selectedEmailSignature.assignedEmails) {
        return [];
      }
      const ids = uniq(this.selectedEmailSignature.assignedEmails.map(elem => elem.businessProcessId));
      if (this.selectedEmailSignature.isDefault) {
        ids.push(DEFAULT);
      }
      return ids;
    },
    assignedEmails() {
      if (!this.selectedEmailSignature || !this.selectedEmailSignature.assignedEmails) {
        return [];
      }
      const emails = uniq(this.selectedEmailSignature.assignedEmails.map(elem => elem.email));
      if (this.selectedEmailSignature.isDefault) {
        emails.push(this.smtpDetails.defaultFromEmailAddress);
      }
      return emails;
    },
    name() {
      return this.selectedEmailSignature.name || '';
    },
    emptyError() {
      if (!this.error) {
        return null;
      }

      return this.$t('errorEmpty');
    },
    availableSmtpEmails() {
      if (!this.smtpDetails.businessProcesses) {
        return [];
      }
      const filteredArray = this.smtpDetails.businessProcesses.filter(bp =>
        this.selectedBusinessProcessesIds.includes(bp.id)
      );
      const result = filteredArray.reduce((accum, bp) => {
        if (bp.defaultBPFromEmailAddress !== '') {
          accum.push({
            email: bp.defaultBPFromEmailAddress,
            businessProcessId: bp.id,
            businessProcessName: bp.name
          });
        }
        accum = accum.concat(
          bp.alternateBPFromEmailAddressAliases.map(email => ({
            email,
            businessProcessId: bp.id,
            businessProcessName: bp.name
          }))
        );
        return accum;
      }, []);
      if (this.selectedBusinessProcessesIds.some(id => id === DEFAULT)) {
        result.push({
          email: this.smtpDetails.defaultFromEmailAddress,
          businessProcessId: DEFAULT,
          businessProcessName: this.$t('default')
        });
      }
      return result;
    },
    isSmtpEnabled() {
      return this.smtpDetails.outgoingEmailMode === 1;
    },
    emailBusinessProcesses() {
      const defaultEmail = { outgoingMediaTypes: ['Email'], id: DEFAULT, name: this.$t('defaultEmailSignature') };
      return [defaultEmail, ...this.businessProcesses];
    }
  },
  methods: {
    ...mapActions(['updateEmailSignature', 'overwriteEmailSignature', 'overwriteDefaultSignature']),
    onBusinessProcessChange(value) {
      if (this.isSmtpEnabled) {
        this.selectedBusinessProcessesIds = value;
      } else {
        this.saveNonSmtp(value);
      }
    },
    updateTemplateName: debounce(function(value) {
      if (value.length > 0) {
        this.error = false;
        this.updateEmailSignature({ field: 'name', value });
      } else {
        this.error = true;
      }
    }, 250),
    confirmBPOverwrite() {
      this.showOverwriteDialog = false;
      let setAsDefault = false;
      this.businessProcessesToOverwrite.forEach(el => {
        if (el.businessProcessId === DEFAULT) {
          setAsDefault = true;
          this.overwriteDefaultSignature();
        } else {
          this.overwriteEmailSignature(el);
        }
      });
      const value = this.assignedEmailsToSave.filter(elem => elem.businessProcessId !== DEFAULT);
      this.updateEmailSignature({ field: 'assignedEmails', value, setAsDefault });
      this.businessProcessesToOverwrite = [];
      this.assignedEmailsToSave = [];
    },
    cancelBPOverwrite() {
      this.showOverwriteDialog = false;
      const signaturesToSave = this.assignedEmailsToSave.filter(elem => {
        const existsInOverwrite = this.businessProcessesToOverwrite.find(
          customizedBP => customizedBP.businessProcessId === elem.businessProcessId && elem.email === customizedBP.email
        );
        return !existsInOverwrite;
      });
      const remainingSignatures = signaturesToSave.filter(elem => elem.businessProcessId !== DEFAULT);
      this.updateEmailSignature({ field: 'assignedEmails', value: remainingSignatures });
      this.businessProcessesToOverwrite = [];
      this.assignedEmailsToSave = [];
    },
    checkIfEmailSignatureWillBeOverwritten(items) {
      const aux = [];
      items.forEach(item => {
        const signatureToOverwrite = this.customizedSignatureBusinessProcesses.find(customizedBP => {
          const result = customizedBP.assignedEmails.filter(
            elem => elem.businessProcessId === item.businessProcessId && elem.email === item.email
          );
          return result.length > 0;
        });
        if (signatureToOverwrite) {
          aux.push({ ...item, signatureId: signatureToOverwrite._id });
        }
      });
      const defaultItem = items.find(
        item => item.businessProcessId === DEFAULT && !this.selectedEmailSignature.isDefault
      );
      if (defaultItem) {
        aux.push(defaultItem);
      }
      this.businessProcessesToOverwrite = aux;
      return aux.length > 0;
    },
    onAssignedEmailsChange(items) {
      if (this.checkIfEmailSignatureWillBeOverwritten(items)) {
        this.showOverwriteDialog = true;
        this.assignedEmailsToSave = items;
      } else {
        if (this.selectedEmailSignature.isDefault) {
          items = items.filter(i => i.businessProcessId !== DEFAULT);
        }
        this.updateEmailSignature({ field: 'assignedEmails', value: items });
      }
    },
    checkIfBusinessProcessWillBeOverwritten(items) {
      const aux = [];
      items.forEach(item => {
        const signatureToOverwrite = this.customizedSignatureBusinessProcesses.find(customizedBP => {
          const result = customizedBP.assignedEmails.filter(
            elem =>
              elem.businessProcessId === item.businessProcessId &&
              elem.email === this.smtpDetails.defaultFromEmailAddress
          );
          return result.length > 0;
        });
        if (signatureToOverwrite) {
          aux.push({ ...item, signatureId: signatureToOverwrite._id });
        }
      });

      const defaultItem = items.find(
        item => item.businessProcessId === DEFAULT && !this.selectedEmailSignature.isDefault
      );
      if (defaultItem) {
        aux.push(defaultItem);
      }
      this.businessProcessesToOverwrite = aux;
      return aux.length > 0;
    },
    saveNonSmtp(items) {
      items = items.map(i => {
        const aux = this.businessProcesses.find(bp => bp.id === i) || { name: this.$t('default') };
        return {
          businessProcessId: i,
          email: this.smtpDetails.defaultFromEmailAddress,
          businessProcessName: aux.name
        };
      });
      if (this.checkIfBusinessProcessWillBeOverwritten(items)) {
        this.showOverwriteDialog = true;
        this.assignedEmailsToSave = items;
      } else {
        if (this.selectedEmailSignature.isDefault) {
          items = items.filter(i => i.businessProcessId !== DEFAULT);
        }
        this.updateEmailSignature({ field: 'assignedEmails', value: items });
      }
    }
  }
};
</script>

<style scoped>
.text-container {
  font-size: 12px;
}
.signature-header-item {
  flex-basis: 300px;
}
.signature-edit-item {
  flex-basis: 300px;
}
</style>
